import { useCallback, useMemo, useState } from 'react'

import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import moment from 'moment'
import { useSelector } from 'react-redux'
import routes from 'Config/routes'
import { isUserSupply } from 'Components/utils/roles'
import { isProviderLogirest } from 'Components/utils/order'
import { ORDER_STATUS, CANCEL_ORDER_ID } from 'Config/constants'
import { confirmNoConformities } from 'Services/api'
import { getApiErrorMessage } from 'Config/utils'

const useCancelOrder = order => {
  const isFetching = useSelector(state => state.delivery.isUpdatingDelivery)
  const user = useSelector(state => state.auth.user)
  const [isSubmiting, setIsSubmiting] = useState(false)
  const selectedCenter = useSelector(state => state.auth.selectedCenter)
  const blockFromProviderOrder = useSelector(
    state =>
      state.purchase.providerOrders?.find(o => o.id === order.id && o.blockCancelOrderButton === true)
        ?.blockCancelOrderButton || false
  )
  const notServedFromProviderOrder = useSelector(
    state =>
      state.purchase.providerOrders?.find(o => o.id === order.id && o.blockOrderNotServedButton === true)
        ?.blockOrderNotServedButton || false
  )
  const history = useHistory()

  const getCanCancelIfLogirestOrder = useCallback(() => {
    if (isProviderLogirest(order)) {
      const beforeDate = moment() <= moment(order.delivery, 'YYYYMMDD').substract(1, 'days')
      return beforeDate
    }
    return true
  }, [order])

  const showCancelOrder = useMemo(() => {
    if (order && order?.created) {
      const isCreatedLessOneMonth = moment() <= moment(order.created, 'YYYYMMDD').add(31, 'days')
      const canCancelIfLogirestOrder = getCanCancelIfLogirestOrder()
      const canCancel =
        (isCreatedLessOneMonth || isUserSupply(user)) && order.state === ORDER_STATUS.PR && canCancelIfLogirestOrder

      if (canCancel) return true
    }
    return false
  }, [getCanCancelIfLogirestOrder, order, user])

  const cancelOrder = useCallback(
    async (description, motive) => {
      const answers = [
        {
          id: 'comment',
          value: description,
          type: 'textarea',
        },
        {
          id: 'dropdown',
          value: motive,
        },
      ]

      const payload = {
        orderId: order.id,
        category: CANCEL_ORDER_ID,
        answers,
        ticketLines: [],
        documentTypeCode: order.documentTypeCode,
        documentCompany: order.documentCompany,
      }

      try {
        setIsSubmiting(true)
        await confirmNoConformities(selectedCenter.id, payload)
        history.push(routes.addTicketingConfirmed(order.id))
      } catch (error) {
        toast.error(getApiErrorMessage(error), { hideProgressBar: true })
      } finally {
        setIsSubmiting(false)
      }
    },
    [history, order.documentCompany, order.documentTypeCode, order.id, selectedCenter.id]
  )

  const blockCancelOrderButton = order?.blockCancelOrderButton || false
  const blockCancelOrderButtonFromProviderOrder = blockFromProviderOrder
  // Pedido completo no servido y cancelación son excluyentes (CMP2201-1844)
  const hasNotServedNonConformity = order?.blockOrderNotServedButton === true || notServedFromProviderOrder

  return {
    cancelOrder,
    isFetching,
    showCancelOrder,
    isUpdatingNC: isSubmiting,
    blockCancelOrderButton,
    blockCancelOrderButtonFromProviderOrder,
    hasNotServedNonConformity,
  }
}

export default useCancelOrder
